import type { FunctionComponent, ReactNode } from 'react';

import { VCS_APP_DISPLAY } from '../../../../shared/vcs/githubApp/apps';
import { useGitHubAppInstallation } from '../../../hooks/api/vcs/githubApp/useGitHubAppInstallation';
import { useStartGitHubAppInstall } from '../../../hooks/api/vcs/githubApp/useStartGitHubAppInstall';
import { ErrorAlert } from '../../generic/ErrorAlert';
import Loader from '../../generic/Loader';
import InstallGithubAppFallback from '../import/Fallbacks/InstallGithubAppFallback';

import styles from './FossabotPrsConnectionGate.module.less';

type Props = {
  children: ReactNode;
};

/**
 * Gates the fossabot PRs page on the org having the fossabot GitHub App installed.
 * Without the app there is nothing for fossabot to open PRs against, so an org that
 * has not connected gets the same install CTA as the GitHub App import flow rather
 * than a permanently empty page.
 *
 * The installation check fires on mount, so only render this once the page's own
 * feature-flag and permission gates have passed.
 */
export const FossabotPrsConnectionGate: FunctionComponent<Props> = ({ children }) => {
  const { data, isLoading, isError } = useGitHubAppInstallation('fossabot');
  const { mutate: startInstall } = useStartGitHubAppInstall();

  /*
   * !isError guards a stale-success refetch: react-query keeps the last successful
   * `data` around while a background refetch errors.
   */
  const isConnected = Boolean(data?.installed) && !isError;

  /*
   * Wait for the check rather than assuming an answer, otherwise an org that is
   * already connected sees the install CTA flash on the way in. A failed check
   * leaves the connection unknown, and the CTA is the safe answer either way,
   * since installing an app the org already has just returns them here. The alert
   * keeps an already-connected org from reading the CTA as the whole story.
   */
  const renderContent = (): ReactNode => {
    if (isLoading) {
      return <Loader />;
    }
    if (!isConnected) {
      return (
        <>
          {isError && <ErrorAlert status={{ error: 'Failed to load GitHub App status. Please try again.' }} />}
          <div className={styles.card}>
            <InstallGithubAppFallback
              appDisplay={VCS_APP_DISPLAY.fossabot}
              onInstall={() => startInstall('fossabot')}
            />
          </div>
        </>
      );
    }
    return children;
  };

  // The page container wraps every state so the loader and the CTA sit in the same padded column.
  return <div className="padding-top-xl container">{renderContent()}</div>;
};
